/* =========================================================
   Compliance & best practices — per-organization documents.

   · Visible only to ETC Admin and Partner Manager
   · Upload / delete: ETC Admin only · no document limit
   · Columns: Fecha publicación · Compliance doc's ·
     % de Verificación · Acciones · Fecha de verificación
   · Verification bar: red <33% · yellow 33–99% · green 100%
   · Download unlocks only at 100%
   ========================================================= */
import React from 'react';
import type { ComplianceDoc } from '@/types';
import { ic, sized } from '@/components/Icons';
import { Modal } from '@/components/ui';
import { ds, notasDeLaUltimaPublicacion } from '@/data';
import { useI18n } from '@/i18n';
import { useAppStore } from '@/store/useAppStore';
import { fmtD, fmtDT, fmtSize, pctColor, triggerDownload } from './helpers';
import { ViewerModal } from './ViewerModal';

const MAX_UPLOAD = 15 * 1024 * 1024;

/* ---------- mini progress bar (table cell) ---------- */

function MiniBar({ pct }: { pct: number }) {
  const color = pctColor(pct);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, background: 'var(--bg-2)', borderRadius: 99, height: 6, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', background: color, height: '100%', borderRadius: 99, transition: 'width .3s ease' }} />
      </div>
      <span className="mono" style={{ fontSize: 11.5, fontWeight: 800, minWidth: 36, textAlign: 'right', color }}>{pct}%</span>
    </div>
  );
}

/* ---------- verification date cell + floating history ---------- */

function VerifiedCell({ doc }: { doc: ComplianceDoc }) {
  const { lang } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  const history = (doc.history || []).slice().sort().reverse(); // newest first
  const current = doc.verifiedAt || doc.verifiedDate; // null while re-verifying
  const clickable = history.length > 1 || (!current && history.length >= 1);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const closeNow = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', closeNow, true);
    window.addEventListener('resize', closeNow);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', closeNow, true);
      window.removeEventListener('resize', closeNow);
    };
  }, [open]);

  if (!current && history.length === 0) return <span style={{ color: 'var(--text-3)' }}>—</span>;

  function toggle() {
    if (!clickable) return;
    if (open) {
      setOpen(false);
      return;
    }
    const r = wrapRef.current!.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.right - 252, window.innerWidth - 260)) });
    setOpen(true);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={clickable ? open : undefined}
        data-tip={
          clickable
            ? lang === 'es'
              ? `${history.length} verificaci${history.length === 1 ? 'ón' : 'ones'} · clic para ver el historial`
              : `${history.length} verification${history.length === 1 ? '' : 's'} · click to view history`
            : undefined
        }
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 6px', margin: '-3px -6px',
          border: 'none', background: open ? 'var(--surface-2)' : 'transparent', borderRadius: 6,
          cursor: clickable ? 'pointer' : 'default', font: 'inherit', color: 'inherit',
        }}
      >
        {current ? (
          <>
            <span style={{ color: 'var(--ok)', display: 'inline-flex' }}>{sized(ic.check, 13)}</span>
            <span className="mono" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDT(current)}</span>
          </>
        ) : (
          <span style={{ color: 'var(--text-3)' }}>—</span>
        )}
        {clickable ? (
          <>
            <span className="chip mono" style={{ fontSize: 9, padding: '1px 5px' }}>×{history.length}</span>
            <span style={{ display: 'inline-flex', color: 'var(--text-3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }}>
              {sized(ic.chevD, 12)}
            </span>
          </>
        ) : null}
      </button>

      {open && pos ? (
        <div
          style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 80, width: 252,
            background: 'var(--surface)', border: '1px solid var(--line-2)', borderRadius: 9,
            boxShadow: 'var(--shadow-3)', overflow: 'hidden',
          }}
        >
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', fontSize: 10.5, fontWeight: 800, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em', background: 'var(--surface-2)' }}>
            {lang === 'es' ? 'Historial de verificaciones' : 'Verification history'}
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {history.map((h, i) => (
              <div
                key={h + i}
                style={{
                  padding: '7px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 8, borderBottom: i < history.length - 1 ? '1px solid var(--line)' : 'none', fontSize: 12,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--ok)', display: 'inline-flex' }}>{sized(ic.check, 12)}</span>
                  <span className="mono">{fmtDT(h)}</span>
                </span>
                {i === 0 && current ? <span className="chip ok" style={{ fontSize: 9 }}>{lang === 'es' ? 'Actual' : 'Latest'}</span> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------- main card ---------- */

/* Fuera del componente a propósito: definida dentro, cada render creaba
   un tipo nuevo y React remontaba la tabla entera. Aquí solo son
   encabezados y no se notaba, pero es el mismo defecto que hacía
   imposible teclear en el formulario de usuarios. */
function TH({ children, align, width }: {
  children?: React.ReactNode; align?: 'left' | 'right'; width?: number;
}) {
  return (
    <th
      style={{
        textAlign: align || 'left', padding: '10px 14px', width,
        fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '.05em',
        borderBottom: '1px solid var(--tbl-head-d)', background: 'var(--tbl-head)', whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

export function ComplianceCard({ orgId, orgName }: { orgId: string; orgName: string }) {
  const { lang } = useI18n();
  const role = useAppStore((s) => s.role);
  const addToast = useAppStore((s) => s.addToast);

  const canView = role === 'ETC_ADMIN' || role === 'PARTNER_MGR';
  const canUpload = role === 'ETC_ADMIN';
  const [subiendo, setSubiendo] = React.useState(false);

  const [, setVersion] = React.useState(0);
  const bump = () => setVersion((v) => v + 1);
  const [viewingId, setViewingId] = React.useState<string | null>(null);
  const [confirmDel, setConfirmDel] = React.useState<ComplianceDoc | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setViewingId(null);
    setConfirmDel(null);
  }, [orgId]);

  if (!canView) return null;

  const docs = ds.listCompliance(orgId);
  const viewingDoc = viewingId ? docs.find((d) => d.id === viewingId) : null;
  const anySessionOnly = docs.some((d) => d.sessionOnly);
  const verifiedCount = docs.filter((d) => d.pct === 100).length;

  function acceptFile(file: File | undefined | null) {
    if (!file) return;
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    const isDocx =
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || /\.docx$/i.test(file.name);
    if (!isPdf && !isDocx) {
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'Formato no válido' : 'Invalid format',
        body: lang === 'es' ? 'El documento debe ser un archivo PDF o DOCX.' : 'The document must be a PDF or DOCX file.',
      });
      return;
    }
    if (file.size > MAX_UPLOAD) {
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'Archivo muy grande' : 'File too large',
        body: lang === 'es' ? 'Máximo 15 MB.' : 'Max 15 MB.',
      });
      return;
    }
    // Contra el servidor esto sube 15 MB: hay que esperar respuesta antes
    // de cantar victoria, y decirlo si falla.
    setSubiendo(true);
    ds.addComplianceDoc(orgId, file)
      .then(() => {
        bump();
        addToast({
          kind: 'ok',
          title: lang === 'es' ? 'Documento publicado' : 'Document published',
          body: file.name,
          details: orgName,
        });
        // Si no había a quién avisar, el administrador tiene que enterarse
        // ahora y no dentro de un mes preguntándose por qué nadie lo leyó.
        for (const n of notasDeLaUltimaPublicacion()) {
          // Persistente: si dice que nadie va a ver el documento, ese es
          // justo el aviso que no puede desaparecer solo a los 4 segundos.
          addToast({
            kind: 'info', title: lang === 'es' ? 'Ten en cuenta' : 'Note',
            body: n, persistente: true,
          });
        }
      })
      .catch((err: unknown) => {
        addToast({
          kind: 'err',
          title: lang === 'es' ? 'No se publicó' : 'Not published',
          body: err instanceof Error ? err.message : String(err),
        });
      })
      .finally(() => setSubiendo(false));
  }

  function removeDoc(doc: ComplianceDoc) {
    ds.removeComplianceDoc(orgId, doc.id);
    if (viewingId === doc.id) setViewingId(null);
    bump();
    addToast({ kind: 'delete', title: lang === 'es' ? 'Documento eliminado' : 'Document removed', body: doc.title || doc.name });
  }

  const tdStyle: React.CSSProperties = { padding: '10px 14px', borderBottom: '1px solid var(--line)', verticalAlign: 'middle' };

  return (
    <div
      className="card"
      style={{ overflow: 'hidden' }}
      onDragOver={(e) => {
        if (canUpload) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (!canUpload) return;
        acceptFile(e.dataTransfer.files?.[0]);
      }}
    >
      {/* header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ color: 'var(--brand-primary)', display: 'inline-flex' }}>{sized(ic.flag, 16)}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Compliance &amp; best practices</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }} className="truncate">
              {docs.length
                ? lang === 'es'
                  ? `${docs.length} documento${docs.length === 1 ? '' : 's'} · ${verifiedCount} verificado${verifiedCount === 1 ? '' : 's'}`
                  : `${docs.length} document${docs.length === 1 ? '' : 's'} · ${verifiedCount} verified`
                : lang === 'es'
                  ? 'Documentos de cumplimiento y buenas prácticas de la organización'
                  : 'Compliance and best-practice documents for this organization'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {!canUpload ? (
            <span className="chip lock" data-tip={lang === 'es' ? 'Tu rol solo permite verificar y descargar los documentos' : 'Your role can only verify and download documents'}>
              {ic.eye} {lang === 'es' ? 'Solo lectura' : 'View only'}
            </span>
          ) : null}
          {anySessionOnly ? (
            <span className="chip" data-tip={lang === 'es' ? 'Algún archivo supera el espacio local: se conserva solo durante esta sesión' : 'A file exceeds local storage: kept for this session only'}>
              {ic.info} {lang === 'es' ? 'Solo sesión' : 'Session only'}
            </span>
          ) : null}
          {canUpload ? (
            <>
              <button className="btn sm" onClick={() => fileRef.current?.click()}>
                {sized(ic.upload, 13)}
                {lang === 'es' ? 'Cargar documento' : 'Upload document'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  acceptFile(f);
                }}
              />
            </>
          ) : null}
        </div>
      </div>

      {docs.length === 0 ? (
        <div
          onClick={() => canUpload && !subiendo && fileRef.current?.click()}
          style={{
            margin: 16, padding: '34px 16px', textAlign: 'center', borderRadius: 10,
            border: '2px dashed ' + (dragOver ? 'var(--brand-primary)' : 'var(--line-2)'),
            background: dragOver ? 'var(--brand-primary-l)' : 'var(--surface-2)',
            cursor: canUpload && !subiendo ? 'pointer' : 'default', transition: 'all .15s ease',
            opacity: subiendo ? 0.6 : 1,
          }}
        >
          <div style={{ display: 'inline-flex', color: dragOver ? 'var(--brand-primary)' : 'var(--text-3)', marginBottom: 10 }}>
            {sized(canUpload ? ic.upload : ic.eye, 30)}
          </div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            {canUpload
              ? lang === 'es' ? 'Cargar el primer documento' : 'Upload the first document'
              : lang === 'es' ? 'Sin documentos publicados' : 'No documents published'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
            {canUpload
              ? lang === 'es'
                ? 'Haz clic o arrastra el archivo aquí · PDF o DOCX · máx. 15 MB · sin límite de documentos'
                : 'Click or drag the file here · PDF or DOCX · max 15 MB · unlimited documents'
              : lang === 'es'
                ? 'El Admin ETC aún no ha publicado documentos para esta organización.'
                : 'ETC Admin has not published documents for this organization yet.'}
          </div>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', background: dragOver ? 'var(--brand-primary-l)' : 'transparent', transition: 'background .15s ease' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <TH width={120}>{lang === 'es' ? 'Fecha publicación' : 'Published'}</TH>
                  <TH>Compliance doc&apos;s</TH>
                  <TH width={170}>{lang === 'es' ? '% de Verificación' : 'Verification %'}</TH>
                  <TH width={230}>{lang === 'es' ? 'Acciones' : 'Actions'}</TH>
                  <TH width={140}>{lang === 'es' ? 'Fecha de verificación' : 'Verified on'}</TH>
                </tr>
              </thead>
              <tbody>
                {docs.map((doc) => (
                  <tr key={doc.id}>
                    <td style={tdStyle}>
                      <span className="mono" style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtD(doc.date)}</span>
                    </td>
                    <td style={{ ...tdStyle, minWidth: 200 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <div style={{ minWidth: 0 }}>
                          <div className="truncate" style={{ fontWeight: 600 }}>{doc.title || doc.name}</div>
                          <div className="truncate mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                            {doc.name} · {fmtSize(doc.size)}
                          </div>
                        </div>
                        <span className="chip lock mono" style={{ fontSize: 9, flexShrink: 0 }}>{(doc.kind || 'pdf').toUpperCase()}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <MiniBar pct={doc.pct || 0} />
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          className="btn sm icon act-verify"
                          onClick={() => setViewingId(doc.id)}
                          aria-label={lang === 'es' ? 'Verificar' : 'Verify'}
                          data-tip={lang === 'es' ? 'Verificar' : 'Verify'}
                        >
                          {sized(ic.eye, 14)}
                        </button>
                        <span data-tip={doc.pct < 100 ? (lang === 'es' ? 'Descarga — se habilita al verificar el 100%' : 'Download — enabled at 100% verified') : undefined}>
                          <button
                            className={'btn sm icon' + (doc.pct >= 100 ? ' act-download' : '')}
                            disabled={doc.pct < 100}
                            onClick={() => {
                              if (doc.pct < 100) return;
                              void ds.loadComplianceFile(orgId, doc.id)
                                .then((url) => triggerDownload(doc, url))
                                .catch((err: unknown) => addToast({
                                  kind: 'err',
                                  title: lang === 'es' ? 'No se pudo descargar' : 'Download failed',
                                  body: err instanceof Error ? err.message : String(err),
                                }));
                              addToast({ kind: 'template', title: lang === 'es' ? 'Descarga iniciada' : 'Download started', body: doc.name });
                            }}
                            aria-label={lang === 'es' ? 'Descarga' : 'Download'}
                            data-tip={doc.pct >= 100 ? (lang === 'es' ? 'Descarga' : 'Download') : undefined}
                          >
                            {sized(ic.download, 14)}
                          </button>
                        </span>
                        {canUpload ? (
                          <button
                            className="btn sm icon act-delete"
                            onClick={() => setConfirmDel(doc)}
                            aria-label={lang === 'es' ? 'Eliminar' : 'Delete'}
                            data-tip={lang === 'es' ? 'Eliminar' : 'Delete'}
                          >
                            {sized(ic.trash, 14)}
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <VerifiedCell doc={doc} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ padding: '9px 16px', background: 'var(--surface-2)', borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex' }}>{sized(ic.info, 13)}</span>
            {lang === 'es'
              ? 'La descarga se habilita al verificar el 100% del documento. Barra: roja <33% · amarilla 33–99% · verde 100%.'
              : 'Download is enabled once 100% of the document is verified. Bar: red <33% · yellow 33–99% · green 100%.'}
          </div>
        </>
      )}

      {/* delete confirmation */}
      {confirmDel ? (
        <Modal
          open
          onClose={() => setConfirmDel(null)}
          width={460}
          title={lang === 'es' ? 'Eliminar documento' : 'Remove document'}
          footer={
            <>
              <button className="btn" onClick={() => setConfirmDel(null)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</button>
              <button
                className="btn act-delete"
                onClick={() => {
                  const d = confirmDel;
                  setConfirmDel(null);
                  removeDoc(d);
                }}
              >
                {sized(ic.trash, 13)} {lang === 'es' ? 'Eliminar definitivamente' : 'Delete permanently'}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0, fontSize: 13, color: 'var(--text)' }}>
            {lang === 'es' ? '¿Seguro que deseas eliminar este documento?' : 'Are you sure you want to remove this document?'}
          </p>
          <div className="card" style={{ padding: '10px 14px', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: 'var(--brand-primary)', display: 'inline-flex', flexShrink: 0 }}>{sized(ic.flag, 16)}</span>
            <div style={{ minWidth: 0 }}>
              <div className="truncate" style={{ fontWeight: 600, fontSize: 13 }}>{confirmDel.title || confirmDel.name}</div>
              <div className="truncate mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                {confirmDel.name} · {fmtSize(confirmDel.size)} · {fmtD(confirmDel.date)}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-2)', display: 'flex', gap: 6, alignItems: 'flex-start', lineHeight: 1.45 }}>
            <span style={{ color: 'var(--warn)', flexShrink: 0, display: 'inline-flex', marginTop: 1 }}>{sized(ic.warn, 14)}</span>
            <span>
              {lang === 'es'
                ? 'Esta acción no se puede deshacer. Se perderá también el avance de verificación y su historial.'
                : 'This action cannot be undone. The verification progress and its history will also be lost.'}
            </span>
          </div>
        </Modal>
      ) : null}

      {viewingDoc ? (
        <ViewerModal key={viewingDoc.id} orgId={orgId} doc={viewingDoc} onClose={() => setViewingId(null)} onChanged={bump} />
      ) : null}
    </div>
  );
}
